import { createContext, useContext } from "react";

import { escapeRegExp } from "@storyboard/dispatch-view/query";
import { brandPrimary } from "@/ui/tokens";

/** The search in force and the fields it scans, so a cell in one of those columns can mark what matched. */
export type SearchHighlight = { term: string; fields: ReadonlySet<string> };

const NO_HIGHLIGHT: SearchHighlight = { term: "", fields: new Set() };

/**
 * The page's search, read by the table's cells. A context rather than a column prop, so typing re-renders only the
 * cells that read it instead of rebuilding every column definition per keystroke.
 */
export const DispatchSearchHighlight = createContext<SearchHighlight>(NO_HIGHLIGHT);

/** The look of a matched run of text, set on the cell around it so plain and HTML cells mark alike. */
export const SEARCH_MARK_SX = {
  "& mark": {
    bgcolor: `color-mix(in oklch, ${brandPrimary[400]} 40%, transparent)`,
    color: "white",
    fontWeight: 600,
    borderRadius: "2px",
  },
} as const;

/** One run of a cell's text, where it starts in the text, and whether the search matched it. */
export type TextSegment = { text: string; start: number; match: boolean };

/**
 * Splits text into the runs a search term matches and the runs between, matching as the search does: the literal
 * term, case-insensitively. A blank term matches nothing.
 */
export function splitOnMatches(text: string, term: string): TextSegment[] {
  const needle = term.trim();
  if (needle === "" || text === "") return [{ text, start: 0, match: false }];
  // A capturing split puts every match at an odd index, between the runs around it.
  const parts = text.split(new RegExp(`(${escapeRegExp(needle)})`, "i"));
  const segments: TextSegment[] = [];
  let start = 0;
  for (const [index, part] of parts.entries()) {
    if (part !== "") segments.push({ text: part, start, match: index % 2 === 1 });
    start += part.length;
  }
  return segments;
}

/** The term to mark in a column's cells: the search's while it scans that column's field, else none. */
export function useSearchTermFor(field: string | undefined): string {
  const { term, fields } = useContext(DispatchSearchHighlight);
  return field != null && fields.has(field) ? term : "";
}

/** Text with the runs a search matched wrapped in `<mark>`; unchanged when nothing matches. */
export function HighlightedText({ text, term }: { text: string; term: string }) {
  return (
    <>
      {splitOnMatches(text, term).map((segment) =>
        segment.match ? <mark key={segment.start}>{segment.text}</mark> : segment.text,
      )}
    </>
  );
}

/**
 * Wraps every run of a term in an element's text nodes in `<mark>`, for cells whose content is HTML React does not
 * own. Only text nodes are split, so the markup around them is left as it was.
 */
export function markMatches(root: HTMLElement, term: string): void {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  // Collect first: splitting a node while walking would move the walker onto the nodes it just made.
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node instanceof Text) textNodes.push(node);
  }
  for (const node of textNodes) {
    const segments = splitOnMatches(node.data, term);
    if (!segments.some((segment) => segment.match)) continue;
    const fragment = document.createDocumentFragment();
    for (const segment of segments) {
      if (!segment.match) {
        fragment.append(segment.text);
        continue;
      }
      const mark = document.createElement("mark");
      mark.textContent = segment.text;
      fragment.append(mark);
    }
    node.replaceWith(fragment);
  }
}
